import type { DbRecordedEvent, UUID } from "./DatabaseTypes";
import { requireEvidenceDatabase } from "./getDatabase";
import { action, observable } from "mobx";

export interface GetsObjectToDisplay {
	get (index: number): unknown;
	getAsync (index: number): Promise<unknown>;
}

export interface GetsDbEventsByIndex extends GetsObjectToDisplay {
	get: (eventIndex: number) => DbRecordedEvent | undefined;
	getAsync (index: number): Promise<DbRecordedEvent | undefined>;
}

export interface GetsHarEntriesByIndex extends GetsObjectToDisplay {
	get (entryIndex: number): HARFormatEntry | undefined;
	getAsync (entryIndex: number): Promise<HARFormatEntry | undefined>;
}

export interface HasGetsDbEventsOrHasGetHarEntriesByIndex {
	getsDbEventsByIndex: GetsDbEventsByIndex,
	getsHarEntriesByIndex?: GetsHarEntriesByIndex,
}

export class DbEventCachePrefilled implements GetsDbEventsByIndex {
	#eventsByIndex: Map<number, DbRecordedEvent>;
	constructor (readonly events: DbRecordedEvent[]) {
		this.#eventsByIndex = new Map(events.map(event => [event.eventIndex, event]));
	}

	get = (eventIndex: number) => this.#eventsByIndex.get(eventIndex);
	getAsync = async (eventIndex: number) => this.#eventsByIndex.get(eventIndex);
}

export class DbEventCacheForRecording implements GetsDbEventsByIndex {

	constructor (public readonly recordingUuid: UUID) {}

	eventsOrUndefinedByIndex = observable.map<number, DbRecordedEvent | undefined>();
	eventPromiseByIndex = observable.map<number, Promise<DbRecordedEvent | undefined>>();

	protected addAction = action ( (eventIndex: number, event: DbRecordedEvent | undefined) => {
		this.eventsOrUndefinedByIndex.set(eventIndex, event);
	});

	protected add = (event: DbRecordedEvent) => {
		this.addAction(event.eventIndex, event);
	}

	getAsync = async (eventIndex: number): Promise<DbRecordedEvent | undefined> => {
		const cachedEvent = this.eventsOrUndefinedByIndex.get(eventIndex);
		if (cachedEvent != null) {
			return cachedEvent;
		}
		const cachedEventPromise = this.eventPromiseByIndex.get(eventIndex);
		if (cachedEventPromise != null) {
			return cachedEventPromise;
		}
		const getEventPromise = requireEvidenceDatabase().then( db => db.get("events", [this.recordingUuid, eventIndex]) );
		this.eventPromiseByIndex.set(eventIndex, getEventPromise);
		getEventPromise.then( event => {
			this.addAction(eventIndex, event);
		});
		return getEventPromise;
	}
	
	get = (eventIndex: number) => {
		if (!this.eventPromiseByIndex.has(eventIndex)) {			
			this.getAsync(eventIndex);
		}
		return this.eventsOrUndefinedByIndex.get(eventIndex);
	}

}

// interface HasRecordingUuidAndEventIndex {
// 	 recordingUuid: UUID;
// 	 eventIndex: number;
// }

// const keyForRecordingUuidThenEventIndex = ({recordingUuid, eventIndex }: HasRecordingUuidAndEventIndex) =>
// 	`${recordingUuid} ${eventIndex}` as const;
// type KeyForRecordingUuidThenEventIndex = ReturnType<typeof keyForRecordingUuidThenEventIndex>;

// class DbEventsCache {

// 	byIndex = observable.map<KeyForRecordingUuidThenEventIndex, DbRecordedEvent>();

// 	constructor (events?: DbRecordedEvent[]) {
// 		if (events == null) return;
// 		runInAction(() => {
// 			for (const event of events) {
// 				this.byIndex.set(keyForRecordingUuidThenEventIndex(event), event);
// 			}
// 		});
// 	}

// 	protected add = action ( (event: DbRecordedEvent) => {
// 		this.byIndex.set(keyForRecordingUuidThenEventIndex(event), event);
// 	});

// 	protected fetch = async ({recordingUuid, eventIndex }: HasRecordingUuidAndEventIndex) => {
// 		const db = await requireEvidenceDatabase();
// 		const event = await db.get("events", [recordingUuid, eventIndex]);
// 		if (event != null) {
// 			this.add(event);
// 		}
// 	}

// 	get = (hasRecordingUuidAndEventIndex: HasRecordingUuidAndEventIndex) => {
// 		const key = keyForRecordingUuidThenEventIndex(hasRecordingUuidAndEventIndex);
// 		if (!this.byIndex.has(key)) {
// 			this.fetch(hasRecordingUuidAndEventIndex);
// 		}
// 		return this.byIndex.get(key);
// 	}

// 	protected static cachedInstance: DbEventsCache | undefined;
// 	static get instance() {
// 		return this.cachedInstance ?? new DbEventsCache();
// 	}
// 	static get(hasRecordingUuidAndEventIndex: HasRecordingUuidAndEventIndex) {
// 		return this.instance.get(hasRecordingUuidAndEventIndex);
// 	}

// }


